import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiHeart, FiPackage, FiShield, FiTruck } from "react-icons/fi";
import { Link, useParams } from "react-router-dom";
import Accordion from "../components/common/Accordion";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Button from "../components/common/Button";
import EmptyState from "../components/common/EmptyState";
import Modal from "../components/common/Modal";
import PageMeta from "../components/common/PageMeta";
import QuantityPicker from "../components/common/QuantityPicker";
import RatingStars from "../components/common/RatingStars";
import ProductGallery from "../components/product/ProductGallery";
import ReviewSummary from "../components/product/ReviewSummary";
import VariantSelector from "../components/product/VariantSelector";
import ProductGrid from "../components/product/ProductGrid";
import siteConfig, { formatCurrency } from "../config/siteConfig";
import products, { categoryImages, getProductById, getProductBySlug } from "../data/products";
import { useCommerce } from "../hooks/useCommerce";

const AttributeRow = ({ label, value }) => {
  const output = Array.isArray(value) ? value.join(" · ") : value;
  if (!output) return null;
  return <div className="product-attributes__row"><dt>{label}</dt><dd>{output}</dd></div>;
};

const ProductPage = ({ legacy = false }) => {
  const params = useParams();
  const product = legacy ? getProductById(params.id) : getProductBySlug(params.slug);
  const [selectedVariant, setSelectedVariant] = useState(product?.variants?.[0] || null);
  const [quantity, setQuantity] = useState(1);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const commerce = useCommerce();

  useEffect(() => {
    setSelectedVariant(product?.variants?.[0] || null);
    setQuantity(1);
  }, [product]);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    const images = [...product.images, categoryImages[product.category]].filter(Boolean);
    return [...new Set(images)];
  }, [product]);

  if (!product) {
    return <EmptyState title="Product not found" description="That jar may have moved to another shelf." actionLabel="Shop all spices" actionTo="/shop" />;
  }

  const related = products.filter((item) => item.category === product.category && item.id !== product.id).slice(0, 4);
  const wishlisted = commerce.isWishlisted(product.id);
  const addToCart = () => {
    commerce.addToCart(product, selectedVariant, quantity);
    commerce.openCartDrawer();
  };

  return (
    <div className="product-page">
      <PageMeta title={product.name} description={product.shortDescription} image={product.images[0]} />
      <div className="site-container product-page__breadcrumbs">
        <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Shop", href: "/shop" }, { label: product.category, href: `/collections/${product.category.toLowerCase().replace(/\s+/g, "-")}` }, { label: product.name }]} />
      </div>
      <section className="site-container product-detail">
        <ProductGallery images={galleryImages} name={product.name} />
        <div className="product-detail__info">
          <p className="eyebrow">{product.category} · {product.subcategory}</p>
          <h1>{product.name}</h1>
          <RatingStars rating={product.rating} count={product.reviewCount} />
          <div className="product-detail__price">
            <strong>{formatCurrency(selectedVariant?.price || product.price)}</strong>
            {product.compareAtPrice > (selectedVariant?.price || product.price) && <del>{formatCurrency(product.compareAtPrice)}</del>}
          </div>
          <p className="product-detail__meta-line"><span>SKU {selectedVariant?.sku || product.sku}</span><span>Flat-rate shipping {formatCurrency(siteConfig.shipping.standardRate)}</span></p>
          <p className="product-detail__lead">{product.shortDescription}</p>
          <div className="product-detail__badges">
            {product.badges.map((badge) => <span key={badge}><FiCheck aria-hidden="true" /> {badge}</span>)}
          </div>
          <VariantSelector variants={product.variants} value={selectedVariant} onChange={setSelectedVariant} />
          <button className="product-detail__size-link" type="button" onClick={() => setSizeGuideOpen(true)}>View size & packaging guide</button>
          <div className="product-detail__purchase">
            <QuantityPicker value={quantity} onChange={setQuantity} max={selectedVariant?.stock || 10} />
            <Button size="large" fullWidth onClick={addToCart}>Add to cart · {formatCurrency((selectedVariant?.price || product.price) * quantity)}</Button>
            <button className={`product-detail__wishlist ${wishlisted ? "is-active" : ""}`} type="button" onClick={() => commerce.toggleWishlist(product.id)} aria-pressed={wishlisted}>
              <FiHeart aria-hidden="true" /> {wishlisted ? "Saved to wishlist" : "Save for later"}
            </button>
          </div>
          <div className="product-detail__promises">
            <span><FiTruck aria-hidden="true" /><strong>Fast dispatch</strong><small>Usually leaves in 1–2 days</small></span>
            <span><FiShield aria-hidden="true" /><strong>Pantry promise</strong><small>We want you to love the flavor</small></span>
            <span><FiPackage aria-hidden="true" /><strong>Packed fresh</strong><small>Sealed in small batches</small></span>
          </div>
          <div className="product-detail__accordions">
            <Accordion title="The flavor" defaultOpen><p>{product.description}</p></Accordion>
            <Accordion title="Origin & ingredients">
              <dl className="product-attributes">
                <AttributeRow label="Origin" value={product.attributes.origin} />
                <AttributeRow label="Ingredients" value={product.attributes.ingredients} />
                <AttributeRow label="Form" value={product.attributes.form} />
                <AttributeRow label="Flavor notes" value={product.attributes.flavorNotes} />
                <AttributeRow label="Heat" value={product.attributes.heatLevel} />
                <AttributeRow label="Dietary" value={product.attributes.dietary} />
              </dl>
            </Accordion>
            <Accordion title="Storage & care"><p>{product.attributes.storage}</p></Accordion>
            <Accordion title="Shipping & returns"><p>Standard shipping is {formatCurrency(siteConfig.shipping.standardRate)}. Unopened goods may be returned within {siteConfig.returns.windowDays} days.</p><Link to="/shipping">Read the full policy</Link></Accordion>
          </div>
        </div>
      </section>
      <div className="site-container"><ReviewSummary rating={product.rating} count={product.reviewCount} /></div>
      <section className="section related-products"><div className="site-container"><header className="section-heading"><p className="eyebrow">Keep exploring</p><h2>More from this shelf</h2></header><ProductGrid products={related} columns={4} cardProps={{ showDescription: false }} /></div></section>
      <Modal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} title="Jar & refill sizes">
        <div className="size-guide"><p>Our pantry jars are designed for daily use; refill pouches are the better value when you already have a clean, dry jar.</p>{product.variants.map((variant) => <div key={variant.id}><strong>{variant.name}</strong><span>{variant.size}</span><span>{formatCurrency(variant.price)}</span></div>)}</div>
      </Modal>
    </div>
  );
};

export default ProductPage;
